import { createContext, useState, useContext, useMemo, useEffect } from 'react';
import { useMutation, graphql, useLazyLoadQuery, useSubscription } from 'react-relay';
import { useUserContext } from '@vitvio/user-context';
import { getLoggerSafe } from '@vitvio/logging';
import type {
  DemoFunctionalityContextTemplatesAndLocationsQuery,
  DemoFunctionalityContextTemplatesAndLocationsQuery$data,
} from './__generated__/DemoFunctionalityContextTemplatesAndLocationsQuery.graphql';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { IS_DEMO_FLAG } from '@vitvio/feature-flags';
import type { DemoFunctionalityContextDebugDataResetSubscription } from './__generated__/DemoFunctionalityContextDebugDataResetSubscription.graphql';
import type { GraphQLSubscriptionConfig } from 'relay-runtime';

const createDebugDataMutation = graphql`
  mutation DemoFunctionalityContextMutation($input: CreateDebugDataInput!) {
    createDebugData(input: $input) {
      operatingRooms(first: 1) {
        edges {
          node {
            name
          }
        }
      }
    }
  }
`;

const templatesAndLocationsQuery = graphql`
  query DemoFunctionalityContextTemplatesAndLocationsQuery {
    locations {
      id
      name
    }
  }
`;

// TEMPORARY: Once the layout does not break due to data refresh this can be removed
const debugDataResetSubscription = graphql`
  subscription DemoFunctionalityContextDebugDataResetSubscription {
    debugDataReset
  }
`;

interface DemoFunctionalityContextType {
  isInDemoMode: boolean;
  createDebugDataForToday: () => void;
  statusMessage: string;
  numberOfOperatingRooms: number;
  setNumberOfOperatingRooms: (numberOfOperatingRooms: number) => void;
  globalReloadKey: number;
  demoTimeZone: string;
  saveDemoTimeZone: (timezone: string) => void;
  templatesAndLocationsData: DemoFunctionalityContextTemplatesAndLocationsQuery$data;
  setGlobalReloadKey: (globalReloadKey: number) => void;
  setStatusMessage: (statusMessage: string) => void;
  setDemoTimeZone: (demoTimeZone: string) => void;
}

const DemoFunctionalityContext = createContext<DemoFunctionalityContextType | null>(null);

interface DemoFunctionalityProviderProps {
  children: React.ReactNode;
  demoOverride: boolean;
}

function DemoFunctionalityProvider({ children, demoOverride }: DemoFunctionalityProviderProps) {
  const { getFlag } = useKindeAuth();

  const templatesAndLocationsData = useLazyLoadQuery<DemoFunctionalityContextTemplatesAndLocationsQuery>(
    templatesAndLocationsQuery,
    {},
  );

  const subscriptionConfig: GraphQLSubscriptionConfig<DemoFunctionalityContextDebugDataResetSubscription> = useMemo(
    () => ({
      subscription: debugDataResetSubscription,
      variables: {},
      onNext: (_data) => {
        setGlobalReloadKey((prev) => prev + 1);
      },
    }),
    [],
  );

  useSubscription(subscriptionConfig);

  const isInDemoMode = demoOverride || (getFlag && getFlag(IS_DEMO_FLAG, false, 'b').value) || false;

  const { hospitalTimezone, refreshUserContext } = useUserContext();

  const [commit] = useMutation(createDebugDataMutation);
  const [statusMessage, setStatusMessage] = useState('');
  // used to trigger a refetch of hospital data in Mission Control
  const [globalReloadKey, setGlobalReloadKey] = useState(0);
  const [numberOfOperatingRooms, setNumberOfOperatingRooms] = useState(4);
  const [demoTimeZone, setDemoTimeZone] = useState(hospitalTimezone);

  // Sync demoTimeZone when hospitalTimezone changes (e.g., when user switches hospitals)
  useEffect(() => {
    setDemoTimeZone(hospitalTimezone);
  }, [hospitalTimezone]);

  function saveDemoTimeZone(timezone: string): void {
    setDemoTimeZone(timezone);
  }

  async function createDebugDataForToday() {
    const logger = getLoggerSafe();
    setStatusMessage('Setting up demo data');
    await new Promise((resolve, reject) => {
      commit({
        variables: {
          input: { template: 'DEMO2024', timezone: demoTimeZone },
        },
        onCompleted: (response, errors) => {
          if (errors) {
            logger?.error('GraphQL errors during debug data creation', { errors });
            reject(errors); // Reject the promise on errors
          } else {
            logger?.debug('Data set up successfully', { response });
            resolve(response); // Resolve the promise on success
          }
        },
        onError: (error) => {
          logger?.error(error as Error, { context: 'createDebugData', timezone: demoTimeZone });
          reject(error); // Reject the promise on error
        },
      });
    });
    refreshUserContext();
    setStatusMessage('');

    setGlobalReloadKey((prev) => prev + 1);
  }

  return (
    <DemoFunctionalityContext.Provider
      value={{
        isInDemoMode,
        statusMessage,
        createDebugDataForToday,
        numberOfOperatingRooms,
        setNumberOfOperatingRooms,
        globalReloadKey,
        demoTimeZone,
        saveDemoTimeZone,
        templatesAndLocationsData,
        setGlobalReloadKey,
        setStatusMessage,
        setDemoTimeZone,
      }}
    >
      {children}
    </DemoFunctionalityContext.Provider>
  );
}

function useDemoFunctionalityContext() {
  const context = useContext(DemoFunctionalityContext);
  if (!context) throw new Error('Debug Menu Settings Context was used outside the surgery provider.');
  return context;
}

export { DemoFunctionalityProvider, useDemoFunctionalityContext };
